import React, { useState } from "react";
import { GeoJSON, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import { NavigationArrow, X, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { geocode } from "../../lib/proximity";

const ROUTE_STYLE = { color: "#FF7A00", weight: 5, opacity: 0.95 };
const ROUTE_WARN = { ...ROUTE_STYLE, dashArray: "10 6" };
const CASING = { color: "#0A0D0C", weight: 8, opacity: 0.6 };

export function MyRouteOverlay({ route }) {
  if (!route) return null;
  const startLabel = route.start?.label || "Your starting point";
  return (
    <>
      <GeoJSON
        key={`myr-c-${route.start.lat}-${route.dest}`}
        data={{ type: "Feature", geometry: route.geometry, properties: {} }}
        style={() => CASING}
        interactive={false}
      />
      <GeoJSON
        key={`myr-${route.start.lat}-${route.dest}`}
        data={{ type: "Feature", geometry: route.geometry, properties: {} }}
        style={() => (route.closure_warning ? ROUTE_WARN : ROUTE_STYLE)}
      >
        <Popup>
          <div className="min-w-[180px]" data-testid="my-route-popup">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#FF7A00" }}>
              Your Evacuation Route
            </div>
            <div className="text-[11px] text-tac-dim">From: <span className="text-tac-text font-bold">{startLabel}</span></div>
            <div className="font-display font-bold text-sm text-tac-text">→ {route.dest}</div>
            <div className="text-[11px] text-tac-dim font-mono-tac">{route.distance_mi} mi · ~{route.duration_min} min</div>
          </div>
        </Popup>
      </GeoJSON>
      <Marker
        position={[route.start.lat, route.start.lon]}
        icon={L.divIcon({
          className: "",
          html: `<div style="transform:translate(-50%,-100%);font-size:22px;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.8));">🏠</div>`,
          iconSize: null,
        })}
      >
        <Tooltip
          direction="top"
          offset={[0, -22]}
          permanent
          opacity={0.95}
          className="search-marker-label"
        >
          <span className="font-bold">{startLabel}</span>
        </Tooltip>
        <Popup>
          <div className="min-w-[160px]" data-testid="my-route-start-popup">
            <div className="text-[10px] font-bold uppercase tracking-wider text-tac-dim mb-1">
              Route start
            </div>
            <div className="text-sm text-tac-text font-bold">{startLabel}</div>
          </div>
        </Popup>
      </Marker>
    </>
  );
}

export default function MyEvacRouteButton({ map, onRoute }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [route, setRoute] = useState(null);

  const buildFrom = async (lat, lon, label = null) => {
    setBusy(true);
    try {
      const { data } = await api.get("/evac-route/from", { params: { lat, lon } });
      // Attach the user's typed address (or "My location") to the route so
      // the start marker on the map shows what they searched for.
      const enriched = { ...data, start: { ...data.start, label } };
      setRoute(enriched);
      onRoute(enriched);
      if (map && enriched.geometry?.coordinates?.length) {
        const latlngs = enriched.geometry.coordinates.map((c) => [c[1], c[0]]);
        map.fitBounds(L.latLngBounds(latlngs).pad(0.15));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build a route from there");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const raw = q.trim();
    if (!raw) return;
    setBusy(true);
    try {
      const res = await geocode(raw);
      if (!res?.length) {
        toast.error("Address not found — try adding the town name");
        setBusy(false);
        return;
      }
      await buildFrom(res[0].lat, res[0].lon, raw);
    } catch {
      toast.error("Address lookup failed");
      setBusy(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not available on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => buildFrom(pos.coords.latitude, pos.coords.longitude, "My location"),
      () => {
        toast.error("Could not get your location — type your address instead");
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const clear = () => {
    setRoute(null);
    onRoute(null);
    setQ("");
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="my-evac-route-btn"
        onClick={() => setOpen((v) => !v)}
        className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border backdrop-blur-xl transition-colors whitespace-nowrap flex items-center gap-1.5 ${
          open || route
            ? "bg-[#FF7A00]/15 border-[#FF7A00]/60 text-[#FF7A00]"
            : "bg-tac-bg/85 border-tac-border text-tac-text hover:border-tac-borderBright"
        }`}
      >
        <NavigationArrow size={12} weight="bold" /> Evac Route
      </button>
      {open && (
        <div
          data-testid="my-evac-route-panel"
          className="absolute right-0 top-full mt-1.5 w-[300px] max-w-[85vw] bg-tac-bg/95 backdrop-blur-xl border border-tac-border rounded-sm shadow-2xl p-3 z-[1200]"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-tac-dim">
              Best route from your address
            </span>
            <button data-testid="my-evac-route-close" onClick={() => setOpen(false)} className="text-tac-faint hover:text-tac-text">
              <X size={13} weight="bold" />
            </button>
          </div>
          <form onSubmit={submit} className="flex gap-1.5">
            <input
              data-testid="my-evac-route-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="123 Main St, Tonasket"
              className="flex-1 bg-tac-surface border border-tac-border rounded-sm px-2 py-1.5 text-[12px] text-tac-text outline-none focus:border-tac-borderBright"
            />
            <button
              type="submit"
              data-testid="my-evac-route-go"
              disabled={busy}
              className="px-3 py-1.5 bg-[#FF7A00] text-black font-bold text-[11px] uppercase rounded-sm disabled:opacity-50"
            >
              {busy ? "…" : "Go"}
            </button>
          </form>
          <button
            data-testid="my-evac-route-gps"
            onClick={useMyLocation}
            disabled={busy}
            className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 border border-tac-border text-tac-dim hover:text-tac-text text-[10px] font-bold uppercase tracking-wider rounded-sm disabled:opacity-50"
          >
            <MapPin size={11} weight="bold" /> Use my location
          </button>
          {route && (
            <div className="mt-2 border-t border-tac-border/60 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  {route.start?.label && (
                    <div className="text-[11px] text-tac-dim">
                      From: <span className="text-tac-text font-bold" data-testid="my-evac-route-from-label">{route.start.label}</span>
                    </div>
                  )}
                  <div className="font-display font-bold text-sm text-tac-text">→ {route.dest}</div>
                  <div className="text-[11px] text-tac-dim font-mono-tac">
                    {route.distance_mi} mi · ~{route.duration_min} min
                    {route.fire && <> · away from {route.fire}</>}
                  </div>
                </div>
                <button data-testid="my-evac-route-clear" onClick={clear} className="text-[9px] font-bold uppercase text-tac-faint hover:text-tac-text border border-tac-border rounded-sm px-1.5 py-1">
                  Clear
                </button>
              </div>
              {route.closure_warning ? (
                <div className="text-[10px] font-bold mt-1" style={{ color: "#FFB703" }}>
                  ⚠ May cross a reported road closure — verify before travel
                </div>
              ) : (
                <div className="text-[10px] mt-1 text-tac-safe">✓ Clear of {route.closures_checked} reported closures</div>
              )}
              <ol className="mt-2 max-h-[200px] overflow-y-auto space-y-1 pr-1" data-testid="my-evac-route-steps">
                {route.steps.map((s, i) => (
                  <li key={i} className="text-[11px] text-tac-dim leading-snug flex gap-1.5">
                    <span className="text-tac-faint font-mono-tac shrink-0">{i + 1}.</span>
                    <span>
                      {s.text}
                      {s.mi >= 0.2 && <span className="text-tac-faint"> — {s.mi} mi</span>}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="text-[9px] text-tac-faint mt-2 leading-snug">
                Advisory only. Always follow law enforcement and posted detours.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
